import { isNonEmpty } from '@sgme/fp';
import type { PublicOffering } from 'components/common-form/sidePanel/actions/trade/tradeButtons.confirming.connect';
import { ProductModificationFormTabState } from 'state/productModificationForm/productModificationForm.common';
import { getFormEnrichedData } from 'state/selectors';
import type { AppState } from 'state/state.model';

const getForm = (state: AppState, tabId: string): ProductModificationFormTabState | undefined =>
  state.productModificationForm[tabId];

export function isProductModification(state: AppState, tabId: string) {
  return getForm(state, tabId) !== undefined;
}
export function getFightingBackJustification(state: AppState, tabId: string) {
  return getForm(state, tabId)?.fightingBackJustification;
}
export function getFightingBackComment(state: AppState, tabId: string) {
  return getForm(state, tabId)?.fightingBackComment;
}
export function getRequestId(state: AppState, tabId: string) {
  return getForm(state, tabId)?.requestId;
}
export function getProductId(state: AppState, tabId: string) {
  return getForm(state, tabId)?.productId;
}

export function getStatus(state: AppState, tabId: string) {
  return getForm(state, tabId)?.status ?? 'INITIALIZED';
}

export function getAstroStatus(state: AppState, tabId: string) {
  return getForm(state, tabId)?.astroStatus;
}

export function getTechnicalError(state: AppState, tabId: string) {
  return getForm(state, tabId)?.technicalError;
}

export function getApplyRequestStartingTimeInMillis(state: AppState, tabId: string) {
  return getForm(state, tabId)?.applyRequestStartedInMillis;
}

export function getApplyRequestDurationInMillis(state: AppState, tabId: string) {
  return getForm(state, tabId)?.applyRequestDurationInMillis;
}

export function getFormQuoteRejections(state: AppState, tabId: string) {
  return getForm(state, tabId)?.errorMessages;
}

export function hasConfirmedPublicOfferingDisclaimer(state: AppState, tabId: string) {
  return getForm(state, tabId)?.hasConfirmedPublicOfferingDisclaimer === true;
}

export function getShowReloadInformation(state: AppState, tabId: string) {
  const status = getStatus(state, tabId);
  const hasAstroErrors = hasErrors(state, tabId);
  return status === 'SUCCESS' && hasAstroErrors === false;
}

export function getShowPublicOfferingDisclaimer(state: AppState, tabId: string): boolean {
  const status = getStatus(state, tabId);
  const enrichedData = getFormEnrichedData(state, tabId);
  const publicOffering = enrichedData?.PublicOffering as PublicOffering[];
  return (
    status === 'INITIALIZED' &&
    isNonEmpty(publicOffering) &&
    publicOffering.some(po => po.PublicOfferingCountry === 'FRA')
  );
}

export function isReady(state: AppState, tabId: string) {
  const status = getStatus(state, tabId);
  const showPublicOfferingDisclaimer = getShowPublicOfferingDisclaimer(state, tabId);
  const publicOfferingDisclaimerConfirmed = hasConfirmedPublicOfferingDisclaimer(state, tabId);
  const fightingbackJustification = getFightingBackJustification(state, tabId);

  return status === 'INITIALIZED' &&
    ((showPublicOfferingDisclaimer === true && publicOfferingDisclaimerConfirmed === true) ||
      showPublicOfferingDisclaimer === false) &&
    fightingbackJustification
    ? true
    : false;
}

export function hasAppliedModification(state: AppState, tabId: string) {
  const status = getStatus(state, tabId);
  switch (status) {
    case 'INITIALIZED':
      return false;
    case 'PENDING':
    case 'SUCCESS':
    case 'FAILED':
      return true;
    default:
      return false as never;
  }
}

export function hasErrors(state: AppState, tabId: string) {
  const form = getForm(state, tabId);
  return isNonEmpty(form?.errorMessages);
}
